/**
 * model-core 模型能力谓词（图片 + 视频）
 *
 * 职责：把「模型有什么能力」从散落的裸 ID / 黑名单常量，收敛为「读 manifest 声明」。
 * 组件不再认识具体模型 id，只调用这些谓词。
 */
import { getImageModelConfig, getVideoModelConfig } from './registry';

/** 是否支持 @ 引用参考图（Banana / GPT Image 系列） */
export const supportsAtMention = (id?: string | null): boolean =>
  !!getImageModelConfig(id).supportAtMention;

/** 是否支持「参考图-角色特征」；默认支持，仅海螺01 等声明 false */
export const supportsReferenceRoleImage = (id?: string | null): boolean =>
  getImageModelConfig(id).supportsReferenceRoleImage !== false;

/** 是否支持参考图（图生图）；默认支持，仅即梦2.1 等声明 false */
export const supportsReferenceImage = (id?: string | null): boolean =>
  getImageModelConfig(id).supportsReferenceImage !== false;

/** 图生图（有参考图）时是否隐藏比例/尺寸参数（即梦3.0 等） */
export const hidesRatioWhenRefImage = (id?: string | null): boolean =>
  !!getImageModelConfig(id).refImageHidesRatio;

/** 是否声明参考模式（ref_type）选项；无 = 不支持单图参考（角色特征/人物长相） */
export const hasRefTypeOptions = (id?: string | null): boolean =>
  !!(getImageModelConfig(id).refTypeOptions?.length);

/** 是否有变化度滑杆（MJ） */
export const hasChangeDegreeToggle = (id?: string | null): boolean =>
  !!getImageModelConfig(id).toggles.changeDegree;

/** 是否有风格化滑杆（MJ） */
export const hasStylizeToggle = (id?: string | null): boolean =>
  !!getImageModelConfig(id).toggles.stylize;

/** 是否有原始模式单选（MJ） */
export const hasRawToggle = (id?: string | null): boolean =>
  !!getImageModelConfig(id).toggles.raw;

/** 是否有 MJ 系列专属控件（变化度/风格化/原始模式） */
export const hasMJControls = (id?: string | null): boolean =>
  !!getImageModelConfig(id).toggles.changeDegree;

/** 是否有背景参数（GPT Image 2.5 系列） */
export const hasBackgroundOption = (id?: string | null): boolean =>
  !!(getImageModelConfig(id).backgroundOptions?.length);

/* ── 视频能力谓词 ────────────────────────────────────────────────────────────── */

/** 是否支持尾帧图；默认支持，仅 field_map.tail_image.max=0 的模型（Hailuo 2.3）声明 false */
export const supportsTailFrame = (id?: string | null): boolean =>
  getVideoModelConfig(id).capabilities.tailFrame !== false;

/** 是否支持首帧图（历史所有模型均支持） */
export const supportsFirstFrame = (_id?: string | null): boolean => true;

/** 是否支持参考图（历史所有模型均支持） */
export const supportsRefImage = (_id?: string | null): boolean => true;

/** 是否支持参考视频 */
export const supportsRefVideo = (id?: string | null): boolean =>
  !!getVideoModelConfig(id).capabilities.refVideo;

/** 是否支持参考音频 */
export const supportsRefAudio = (id?: string | null): boolean =>
  !!getVideoModelConfig(id).capabilities.refAudio;

/** 是否支持参考文档（Wan3.0 家族） */
export const supportsRefDoc = (id?: string | null): boolean =>
  !!getVideoModelConfig(id).capabilities.refDoc;

/** 是否支持同时生成声音 */
export const supportsSound = (id?: string | null): boolean =>
  !!getVideoModelConfig(id).capabilities.sound;

/** 是否支持保留视频原声 */
export const supportsKeepOriginalSound = (id?: string | null): boolean =>
  !!getVideoModelConfig(id).capabilities.keepOriginalSound;

/** 是否支持真人视频（Seedance 2.x 家族） */
export const supportsRealVideo = (id?: string | null): boolean =>
  !!getVideoModelConfig(id).capabilities.realVideo;

/** 是否支持声音且仅简单声音处理（无保留原声、无真人视频），即可灵3.0/Vidu Q3/Wan3.0 这类 */
export const hasSoundOnly = (id?: string | null): boolean =>
  supportsSound(id) && !supportsKeepOriginalSound(id) && !supportsRealVideo(id);

/** 是否仅支持图生视频（不支持文生视频） */
export const isImageToVideoOnly = (id?: string | null): boolean =>
  !!getVideoModelConfig(id).capabilities.imageToVideoOnly;

/** 是否支持多模态参考模式（可灵3.0 Omni / Wan2.7 / Wan3.0 / Seedance 2.x / MiniMax H3） */
export const supportsMultiModalReference = (id?: string | null): boolean =>
  !!getVideoModelConfig(id).capabilities.multiModalReference;

/** 首尾帧模式下是否强制视频比例 adaptive（MiniMax H3 / Seedance 2.5） */
export const supportsForceAdaptiveKeyframe = (id?: string | null): boolean =>
  !!getVideoModelConfig(id).capabilities.forceAdaptiveKeyframe;

/** 首尾帧模式是否允许仅上传尾帧（无需首帧；仅 MiniMax H3） */
export const supportsTailOnlyKeyframe = (id?: string | null): boolean =>
  !!getVideoModelConfig(id).capabilities.tailOnlyKeyframe;

/** 是否支持多视频/多音频引用（多模态参考模式；Seedance 2.x / MiniMax H3 / Wan2.7 / Wan3.0） */
export const supportsMultiVideo = (id?: string | null): boolean =>
  !!getVideoModelConfig(id).capabilities.multiVideo;
